/**
 * SubLineDetailPage - Detailansicht einer Sub-Strecke
 * 
 * Konsistent mit LineDetailPage:
 * - Gleiches Layout (max-w-5xl mx-auto)
 * - Grunddaten: Name + Übergeordnete Strecke (Dropdown)
 * - Hinweis zu historischen Daten
 * - Gefahrenzone für Deaktivierung
 * 
 * Bei id=new: Redirect zu /admin/lines/new (dort wird angelegt)
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Route, ArrowLeft, RefreshCw, Save, Trash2, AlertTriangle, Info } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

interface Line {
  id: string;
  name: string;
  isActive: boolean;
}

interface SubLine {
  id: string;
  name: string;
  lineId: string;
  line?: Line;
  isActive: boolean;
}

// Alphabetische Sortierung
const sortAlpha = <T extends { name: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));
};

export default function SubLineDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // Redirect /admin/sublines/new zu /admin/lines/new
  useEffect(() => {
    if (id === 'new') {
      navigate('/admin/lines/new', { replace: true });
    }
  }, [id, navigate]);

  const [subLine, setSubLine] = useState<SubLine | null>(null);
  const [allLines, setAllLines] = useState<Line[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form State
  const [name, setName] = useState('');
  const [parentLineId, setParentLineId] = useState('');
  
  // Delete Confirm State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load all lines for dropdown
  const loadLines = async () => {
    try {
      const res = await fetch('/api/admin/lines', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAllLines(data.filter((l: Line) => l.isActive !== false));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Strecken:', err);
    }
  };

  // Load subline details
  const loadSubLine = async () => {
    if (!id || id === 'new') return;
    try {
      const res = await fetch('/api/admin/lines', { credentials: 'include' });
      if (res.ok) {
        const linesData = await res.json();
        let foundSubLine: SubLine | null = null;
        let parentLine: Line | null = null;
        for (const line of linesData) {
          const sl = line.subLines?.find((s: SubLine) => s.id === id);
          if (sl) {
            foundSubLine = { ...sl, line };
            parentLine = line;
            break;
          }
        }
        if (foundSubLine && parentLine) {
          setSubLine(foundSubLine);
          setName(foundSubLine.name);
          setParentLineId(parentLine.id);
        } else {
          navigate('/admin/lines');
        }
      }
    } catch (err) {
      console.error('Fehler beim Laden der Sub-Strecke:', err);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (id && id !== 'new') {
        setLoading(true);
        await loadLines();
        await loadSubLine();
        setLoading(false);
      }
    };
    init();
  }, [id]);

  // Save
  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name ist erforderlich');
      return;
    }
    if (!parentLineId) {
      setError('Übergeordnete Strecke ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const res = await fetch(`/api/admin/sublines/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          lineId: parentLineId,
        }),
      });

      if (res.ok) {
        navigate('/admin/lines');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || data.message || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
    setSaving(false);
  };

  // Delete (soft delete)
  const handleDelete = async () => {
    if (!id) return;
    
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/sublines/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        navigate('/admin/lines');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Fehler beim Deaktivieren');
        setDeleteConfirmOpen(false);
      }
    } catch (err) {
      console.error('Fehler beim Deaktivieren:', err);
    }
    setDeleting(false);
  };

  // Sortierte Hauptstrecken für Dropdown
  const sortedLines = sortAlpha(allLines);

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  if (!subLine) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Sub-Strecke nicht gefunden</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            to="/admin/lines"
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Route className="w-6 h-6" />
              {subLine.name}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Sub-Strecke
              {!subLine.isActive && <span className="ml-2 text-red-500">(inaktiv)</span>}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setLoading(true); loadSubLine().then(() => setLoading(false)); }}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !name.trim() || !parentLineId}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Grunddaten */}
        <section className="bg-white rounded-lg border p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 border-b pb-2">Grunddaten</h2>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Mangel Linie 1"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Übergeordnete Strecke <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Route className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <select
                value={parentLineId}
                onChange={(e) => setParentLineId(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Bitte wählen...</option>
                {sortedLines.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
            {parentLineId && (
              <Link
                to={`/admin/lines/${parentLineId}`}
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1"
              >
                <Route className="w-3 h-3" />
                Hauptstrecke öffnen
              </Link>
            )}
          </div>
        </section>

        {/* Hinweis */}
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Hinweis zu historischen Daten</p>
            <p className="mt-1">
              Änderungen am Streckenstamm wirken sich nicht auf bereits erfasste Daten aus.
              Diese enthalten Snapshot-Daten zum Zeitpunkt der Erfassung.
            </p>
          </div>
        </div>

        {/* Delete Section */}
        <section className="bg-white rounded-lg border border-red-200 p-6">
          <h2 className="font-semibold text-red-600 mb-2">Gefahrenzone</h2>
          <p className="text-sm text-slate-600 mb-4">
            Das Deaktivieren einer Sub-Strecke entfernt sie aus der Auswahl. 
            Bereits erfasste Daten bleiben unverändert.
          </p>
          <button
            onClick={() => setDeleteConfirmOpen(true)}
            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Sub-Strecke deaktivieren
          </button>
        </section>
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Sub-Strecke deaktivieren?</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                <strong>"{subLine.name}"</strong> wird deaktiviert.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Die Sub-Strecke kann nicht mehr für neue Erfassungen verwendet werden.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deaktivieren...' : 'Deaktivieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
